import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, CheckIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
import { trackPage } from '../utils/analytics';
import { UrgencyCountdownBar } from '../components/cdv/UrgencyCountdownBar';
import { LoomEmbed } from '../components/cdv/LoomEmbed';
import { CdvCtaButton } from '../components/cdv/CdvCtaButton';
import { CdvFaq } from '../components/cdv/CdvFaq';
import { InsideAcademy } from '../components/cdv/InsideAcademy';
import { OfferAndPricing } from '../components/cdv/OfferAndPricing';
import { WhatsAppFloatButton } from '../components/cdv/WhatsAppFloatButton';
import { CdvExitModal } from '../components/cdv/CdvExitModal';
import { CdvHeader } from '../components/cdv/CdvHeader';
import { useExitIntent } from '../hooks/useExitIntent';
import { stages, testimonials, VSL_LOOM_ID } from '../data/academiaCdv';
const ease: [number, number, number, number] = [0.23, 1, 0.32, 1];
export function AcademiaCDV() {
  const {
    open: exitOpen,
    close: closeExit
  } = useExitIntent();
  useEffect(() => {
    initMetaPixel();
    trackPageView();
    trackPage('/academia-cdv');
  }, []);
  return <div className="min-h-screen w-full bg-black font-sans text-white" data-id="element-253" data-mp="piwdtxr07vs">
      <UrgencyCountdownBar data-id="element-254" data-mp="5h98a4cc33pb" />
      <CdvHeader data-id="element-255" data-mp="5ikcg59oydgy" />

      <main data-id="element-256" data-mp="5j3wpsmtddy5">
        {/* HERO */}
        <section className="px-4 sm:px-6 pt-5 pb-14 sm:pt-8 lg:pt-10 lg:pb-16" data-id="element-257" data-mp="9j48f5ij3b1i">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] lg:grid-rows-[auto_auto] gap-x-12 items-center" data-id="element-258" data-mp="ehpalahcc4sl">
            {/* Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            ease
          }} className="text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-259" data-mp="jhblvedlzayr">
              <h1 className="font-sans text-xl sm:text-3xl lg:text-4xl xl:text-[2.6rem] font-extrabold uppercase leading-[1.12]" data-id="element-260" data-mp="2q89pqduqxr7j">
                Genera el ingreso de{' '}
                <span className="text-gold-metallic" data-id="element-261" data-mp="2d7phs9ly8put">todo un año</span> en una
                sola operación de Bienes Raíces
              </h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-lg font-semibold text-white/90" data-id="element-262" data-mp="2s5hxp2verg9j">
                En menos de 90 días. Incluso si empiezas desde cero y sin
                capital infinito.
              </p>
              <p className="hidden sm:block mt-3 text-base text-white/65 leading-relaxed lg:max-w-xl" data-id="element-263" data-mp="313lifwykk90a">
                El Sistema de 3 Etapas con IA que está permitiendo a
                profesionales en LatAm construir patrimonio real mientras otros
                solo ven cómo la inflación devora sus ahorros.
              </p>
            </motion.div>

            {/* Video */}
            <div className="mt-4 sm:mt-6 lg:mt-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 w-full max-w-3xl mx-auto" data-id="element-264" data-mp="3jwfkxrf0a8nm">
              <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-white/70" data-id="element-265" data-mp="3fs7v1qwtamnt">
                Mira el video
                <ArrowDownIcon className="h-3.5 w-3.5 text-gold-metallic" aria-hidden="true" data-id="element-266" data-mp="7b0g8rsufizf" />
              </p>
              <LoomEmbed loomId={VSL_LOOM_ID} title="Video: Academia de Bienes Raíces" className="shadow-2xl" data-id="element-267" data-mp="3ugrep4uignx8" />
            </div>

            {/* Price + CTA */}
            <div className="mt-4 sm:mt-6 lg:mt-6 lg:col-start-1 lg:row-start-2 lg:self-start w-full max-w-2xl mx-auto lg:mx-0" data-id="element-268" data-mp="3kjvernioha4a">
              <p className="hidden sm:flex mb-4 flex-wrap items-center justify-center lg:justify-start gap-x-3 text-sm text-white/70" data-id="element-269" data-mp="36t9tjs5r4o38">
                <span data-id="element-270" data-mp="2wt99u53z05yi">
                  <span className="font-bold text-gold-metallic" data-id="element-271" data-mp="2wsze00w8v4kw">$47/mes</span>{' '}
                  por tiempo limitado
                </span>
                <span className="h-3.5 w-px bg-white/20" aria-hidden="true" data-id="element-272" data-mp="31rkjofu9mggq" />
                <span data-id="element-273" data-mp="7z2podnj6fg5">
                  o <span className="font-bold text-white" data-id="element-274" data-mp="82goxd0we8jd">$247/año</span> con
                  todo desbloqueado
                </span>
              </p>
              <CdvCtaButton trackingId="cdv_hero_click" data-id="element-275" data-mp="3vcn359fpwl9i" />
            </div>
          </div>
        </section>

        {/* TRANSFORMACIÓN */}
        <section className="px-5 pb-16 md:pb-20" data-id="element-276" data-mp="3qe3irdgrvhj9">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-px overflow-hidden rounded-2xl bg-white/10" data-id="element-277" data-mp="3lfs9p4yaxb23">
            <div className="flex items-center gap-4 bg-black p-6 md:p-8" data-id="element-278" data-mp="3gfea8barn9fo">
              <XIcon className="h-7 w-7 flex-shrink-0 text-[#FF4D5E]" aria-hidden="true" data-id="element-279" data-mp="3cdsos1k61mtv" />
              <div data-id="element-280" data-mp="37h2czqfe9de9">
                <p className="text-xs font-bold uppercase tracking-wider text-white/50" data-id="element-281" data-mp="37hu5uthlwx0u">
                  Pasa de
                </p>
                <p className="mt-1 text-lg text-white/70" data-id="element-282" data-mp="15nl7asatqmm5">
                  No tener ingresos en Bienes Raíces
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-[#0B0B0B] p-6 md:p-8" data-id="element-283" data-mp="1d5t061jjp6m3">
              <CheckIcon className="h-7 w-7 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-284" data-mp="1i4vyz48xmglm" />
              <div data-id="element-285" data-mp="1rx1nu53w0hwl">
                <p className="text-xs font-bold uppercase tracking-wider text-gold-metallic" data-id="element-286" data-mp="1rvi7kei8e6ut">
                  A tener
                </p>
                <p className="mt-1 text-lg font-semibold text-white" data-id="element-287" data-mp="uavz2ynw45if">
                  Múltiples fuentes de ingreso en Bienes Raíces
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* GANCHO Y DOLOR */}
        <section className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-288" data-mp="189lu88r5fv45">
          <div className="max-w-3xl mx-auto space-y-6 text-lg md:text-xl leading-relaxed text-white/75" data-id="element-289" data-mp="2fme39mpt5m7c">
            <p className="font-sans text-2xl md:text-3xl font-bold text-white leading-snug" data-id="element-290" data-mp="2jn6egli8q169">
              Si estás aquí, probablemente quieres cambiar tu situación
              financiera a través de los bienes raíces.
            </p>
            <p data-id="element-291" data-mp="fjcxoo99zly4">
              Tal vez estás empezando desde cero y no sabes por dónde comenzar.
              O ya has realizado algunas operaciones, pero sientes que tus
              resultados están muy por debajo de lo que realmente podrías
              conseguir.
            </p>
            <p data-id="element-292" data-mp="w06ok0p8o0c">
              Trabajas 40, 50 o 60 horas a la semana, ahorras lo que puedes,
              pero sientes que tu cuenta bancaria es un balde con agujeros.
              Miras los precios de las propiedades subir y sientes que el tren
              de la libertad financiera se aleja.
            </p>
            <p data-id="element-293" data-mp="5siczobogues">
              Peor aún, ves a "gurús" prometiendo rentas mágicas, pero cuando
              entras, solo hay teoría de hace 20 años que no funciona en la
              economía de 2026. Sin comunidad que te respalde y sin los
              contratos que protejan tu capital.
            </p>
            <p className="border-l-4 border-[#C8102E] pl-5 text-white" data-id="element-294" data-mp="1org7s4hshk2j">
              El costo de no actuar hoy no es solo dinero; es el tiempo que
              nunca recuperarás con tu familia.
            </p>
          </div>
        </section>

        {/* REVELACIÓN: 3 ETAPAS */}
        <section className="px-5 py-16 md:py-24 bg-[#0B0B0B] border-y border-white/10" data-id="element-295" data-mp="1krxpzhl9lp3x">
          <div className="max-w-5xl mx-auto" data-id="element-296" data-mp="28hs1nvtul7ml">
            <div className="max-w-3xl" data-id="element-297" data-mp="28ew1s1yfw8vi">
              <h2 className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-6" data-id="element-298" data-mp="2cilpkp7aimfh">
                No necesitas pasar años aprendiendo por prueba y error
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-299" data-mp="23h47x2e8icoo">
                Desarrollamos un sistema paso a paso para construir un negocio
                inmobiliario rentable, escalable y sistematizado. Nuestra
                metodología sigue tres etapas muy claras:
              </p>
            </div>

            <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden" data-id="element-300" data-mp="1jr3622psnvi4">
              {stages.map((stage, i) => <li key={stage.name} className="flex flex-col bg-[#0B0B0B] p-6 md:p-8" data-id="element-301" data-mp="19rvv1m6j8cy6" data-mpi={i}>
                  <p className="text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-302" data-mp="14tubl0572bm6">
                    Etapa {i + 1}
                  </p>
                  <p className="mt-2 font-sans text-2xl font-bold text-white" data-id="element-303" data-mp="22idywpf2yicn">
                    {stage.name}
                  </p>
                  <p className="mt-3 text-white/65 leading-relaxed" data-id="element-304" data-mp="1spybvdoxf3kw">
                    {stage.description}
                  </p>
                  <p className="mt-auto pt-4 text-sm font-semibold text-white" data-id="element-305" data-mp="1nr3umg6lxn2w">
                    {stage.goal}
                  </p>
                </li>)}
            </ol>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 text-lg leading-relaxed" data-id="element-306" data-mp="2gfiyq7o7akzg">
              <p className="text-white/70" data-id="element-307" data-mp="2oq9th0zwa30u">
                La verdadera libertad financiera no depende únicamente de vender
                propiedades. También depende de construir ingresos recurrentes.
                Y con{' '}
                <strong className="text-white" data-id="element-308" data-mp="2xrprsadfqeop">Inteligencia Artificial</strong>{' '}
                puedes encontrar en 10 minutos lo que a un agente tradicional le
                toma un mes.
              </p>
              <p className="font-sans text-xl md:text-2xl font-bold text-gold-metallic leading-snug" data-id="element-309" data-mp="3bpefdpyvzzxg">
                No se trata únicamente de cerrar más operaciones. Se trata de
                cerrar mejores operaciones.
              </p>
            </div>
          </div>
        </section>

        <InsideAcademy data-id="element-310" data-mp="3lh1zf5t46eow" />

        {/* PRUEBA SOCIAL */}
        <section aria-labelledby="testimonios-heading" className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-311" data-mp="3qflo3dz3e4ky">
          <div className="max-w-6xl mx-auto" data-id="element-312" data-mp="3vb0ytx1szkd5">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-313" data-mp="2mcgwcn3nm3j9">
              <h2 id="testimonios-heading" className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-4" data-id="element-314" data-mp="2qcrcutp2occd">
                <span className="text-gold-metallic" data-id="element-315" data-mp="2va0yrzd965vg">Testimonios reales:</span>{' '}
                No desde la teoría. Desde la práctica.
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-316" data-mp="350vml865ddj0">
                No quiero que solamente escuches lo que yo digo. Prefiero que
                veas los resultados de quienes ya aplicaron este sistema.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12" data-id="element-317" data-mp="3j1pw5u4xlkj9">
              {testimonials.map(t => <article key={t.loomId} className="flex flex-col" data-id="element-318" data-mp="1jp3vj5ehe5sm">
                  <LoomEmbed loomId={t.loomId} title={`Testimonio: ${t.title}`} data-id="element-319" data-mp="1jqyiep9bc7zc" />
                  <h3 className="mt-4 font-sans text-lg md:text-xl font-bold text-white" data-id="element-320" data-mp="1h4fn5dt1wut2">
                    "{t.title}"
                  </h3>
                  <p className="mt-1 text-sm text-white/60" data-id="element-321" data-mp="1cbcsf2cjffba">
                    {t.name}
                    {t.location && ` · ${t.location}`}
                  </p>
                  {t.result && <p className="mt-2 text-sm font-semibold text-gold-metallic" data-id="element-322" data-mp="12d6sure7el9l">
                      Resultado: {t.result}
                    </p>}
                </article>)}
            </div>
          </div>
        </section>

        <OfferAndPricing data-id="element-323" data-mp="1q3t1okw189kn" />

        {/* FAQ */}
        <section aria-labelledby="faq-heading" className="px-5 py-16 md:py-24 bg-[#0B0B0B] border-y border-white/10" data-id="element-324" data-mp="1q6oy24s8eq6z">
          <div className="max-w-3xl mx-auto" data-id="element-325" data-mp="eqqirj6gn7xb">
            <h2 id="faq-heading" className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-8" data-id="element-326" data-mp="encg09tvwwpc">
              Preguntas frecuentes
            </h2>
            <CdvFaq data-id="element-327" data-mp="h4c746mel5lh" />
          </div>
        </section>

        {/* GARANTÍA */}
        <section className="px-5 py-16 md:py-20" data-id="element-328" data-mp="h29j1s4t9mje">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left" data-id="element-329" data-mp="27uwxipy9x0i">
            <ShieldCheckIcon className="h-16 w-16 flex-shrink-0 text-gold-metallic" aria-hidden="true" data-id="element-330" data-mp="74lc83txkokv" />
            <div data-id="element-331" data-mp="3uam1u7sdq6py">
              <h2 className="font-sans text-2xl md:text-3xl font-bold uppercase mb-3" data-id="element-332" data-mp="3ub45hphdfn7w">
                El riesgo es prácticamente cero
              </h2>
              <p className="text-lg text-white/70 leading-relaxed" data-id="element-333" data-mp="3fjvbz5aclhph">
                Si por cualquier motivo sientes que la comunidad no es para ti,
                puedes cancelar tu membresía cuando quieras. No existe
                permanencia forzosa.
              </p>
            </div>
          </div>
        </section>

        {/* CIERRE */}
        <section className="px-5 py-16 md:py-24 border-t border-white/10" data-id="element-334" data-mp="2rxt288k5ebxb">
          <div className="max-w-5xl mx-auto" data-id="element-335" data-mp="2mzrh7i35w2j7">
            <h2 className="font-sans text-2xl md:text-4xl font-bold uppercase leading-tight mb-10 text-center" data-id="element-336" data-mp="2n12ldj0ircau">
              Tienes dos caminos frente a ti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-337" data-mp="2vzwo7r4g0ywu">
              <div className="rounded-2xl border border-white/10 p-6 md:p-8" data-id="element-338" data-mp="3axgoikqea5wo">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white/50" data-id="element-339" data-mp="3avcah9ys7qio">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-340" data-mp="3jpr1uawl6ri5" /> Camino A
                </p>
                <p className="mt-3 text-white/60 leading-relaxed" data-id="element-341" data-mp="3jrlaoqul2t5v">
                  Seguir intentando ahorrar en un mercado que castiga a los
                  ahorradores, viendo cómo otros se quedan con las mejores
                  oportunidades.
                </p>
              </div>
              <div className="rounded-2xl border-2 border-gold-metallic bg-white/[0.03] p-6 md:p-8" data-id="element-342" data-mp="3omqy5u3mwfw2">
                <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-metallic" data-id="element-343" data-mp="cjheqv707nz3">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-344" data-mp="ldvxx8unninh" /> Camino B
                </p>
                <p className="mt-3 text-white leading-relaxed" data-id="element-345" data-mp="qcyxwevs2yol">
                  Unirte a la Academia, apalancarte en nuestra tecnología y
                  comunidad, y construir un legado que te permita generar el
                  ingreso de un año en una sola operación.
                </p>
              </div>
            </div>

            <p className="mt-12 text-center text-lg md:text-xl text-white/80" data-id="element-346" data-mp="2mcrst5c3oh8">
              La oportunidad de transformar tu futuro financiero puede comenzar
              con la decisión que tomes hoy. El reloj marca las{' '}
              <strong className="text-white" data-id="element-347" data-mp="1e2b89i2udcsh">
                11:59 PM del lunes 12 de octubre
              </strong>
              .
            </p>
            <div className="mt-8 max-w-2xl mx-auto" data-id="element-348" data-mp="33k83vhwccf1x">
              <CdvCtaButton trackingId="cdv_cierre_click" data-id="element-349" data-mp="33iwxrbffav7n" />
            </div>
            <p className="mt-8 text-center font-heading text-xl italic text-gold-metallic" data-id="element-350" data-mp="2ylwrazenvqg0">
              Nos vemos dentro de la comunidad de la Academia de Bienes Raíces.
            </p>
          </div>
        </section>
      </main>

      <WhatsAppFloatButton data-id="element-351" data-mp="2ospo9l7e8mj3" />
      <CdvExitModal open={exitOpen} onClose={closeExit} data-id="element-352" data-mp="2ksxc6yisjy8z" />

      {/* FOOTER */}
      <footer className="w-full border-t border-white/10" data-id="element-353" data-mp="2fu3zv9f8qdae">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center text-center md:text-left" data-id="element-354" data-mp="2fsj5bjsxeucj">
          <div data-id="element-355" data-mp="25vxzwxczvh86">
            <p className="text-lg md:text-2xl font-bold" data-id="element-356" data-mp="25wg4ch9t91r6">
              Bienes Raíces Academy
            </p>
            <p className="text-sm md:text-lg text-white/80" data-id="element-357" data-mp="93wgfumc9301">
              Por Susana Sánchez G
            </p>
          </div>
          <div className="text-center leading-tight" data-id="element-358" data-mp="45uu90tm8nkc">
            <p className="text-base md:text-xl font-bold tracking-[0.15em] text-white" data-id="element-359" data-mp="1yl1pf947xhvc">
              ACADEMIA
            </p>
            <p className="text-lg md:text-2xl font-extrabold tracking-wide text-gold-metallic" data-id="element-360" data-mp="23cjrk0hz9ohw">
              BIENES RAÍCES
            </p>
          </div>
          <p className="text-xs text-white/60 md:text-right" data-id="element-361" data-mp="1okrfbxdhcqke">
            Todos los Derechos Reservados
            <br data-id="element-362" data-mp="2hb9ong4sro69" />
            2026
          </p>
        </div>
      </footer>
    </div>;
}